import React, { useState, useEffect, useRef } from "react";
import WorkerHeader from '../../worker-header/components/index';
import WorkerFooter from "../../worker-footer/components/index";
import { useDispatch, useSelector } from "react-redux";
import "../style/chat.css";
const BACKEND_URL = process.env.REACT_APP_NODE_API;

const WorkerBotChat = () => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [fields, setFields] = useState([]);
  const [step, setStep] = useState(1);
  const [completedData, setCompletedData] = useState(false);
  const [finaldata, setFinaldata] = useState(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const [type, setType] = useState("text");
  const [options, setOptions] = useState([]);
  const [title, setTitle] = useState("");
  const chatContainerRef = useRef(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const sendInitialMessage = async () => {
      setMessages([
        {
          type: "bot",
          text: "Welcome! Please enter the state you’re asking about (e.g., Virginia, Texas, California) so I can provide accurate childcare compliance information."
        }
      ]);
    };

    sendInitialMessage();
  }, []);

  useEffect(() => {
    if (reviewData && modalRef.current) {
      const modal = new window.bootstrap.Modal(modalRef.current);
      modal.show();
    }
  }, [reviewData]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }

    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.type === "bot") {
      speak(lastMessage.text);
    }

  }, [messages]);

  // Scroll chat to bottom when messages update
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const speak = (text) => {
    if (!('speechSynthesis' in window) || !text) return;

    const synth = window.speechSynthesis;

    const speakNow = () => {
      const voices = synth.getVoices();
      // console.log("voices", voices);

      const femaleVoice = voices.find(voice =>
        voice.lang === "en-US" && /female|Google/i.test(voice.name)
      );

      // console.log("female voice", femaleVoice);

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "en-US";
      utterance.rate = 1;
      utterance.pitch = 1;

      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }

      synth.cancel();
      synth.speak(utterance);
    };

    // ✅ If voices already loaded
    if (synth.getVoices().length > 0) {
      speakNow();
    } else {
      // ⏳ Wait for voices
      synth.onvoiceschanged = () => {
        speakNow();
      };
    }
  };

  const MicButton = () => (
    <button
      className="btn btn-secondary ms-2"
      onClick={startListening}
    >
      {listening ? "Listening..." : "🎤"}
    </button>
  );

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert("Speech Recognition not supported in this browser.");
      return;
    }
    console.log('startlistening');

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.trim();
      console.log("Transcript:", transcript);

      // Handle speech based on input type
      if (type === "text") {
        setInput(transcript);
      }

      setListening(false);
    };

    recognition.onend = () => setListening(false);

    recognition.start();
    setListening(true);
    recognitionRef.current = recognition;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };


  // Handle send
  const handleSend = async () => {
    if (!input.trim()) return;
    let displayText = input;


    setMessages(prev => [...prev, { type: "user", text: displayText }]);
    setInput(""); // clear input

    setMessages(prev => [...prev, { type: "bot", loading: true }]);

    try {
      setLoading(true);

      const res = await fetch(`${BACKEND_URL}/compliance/load`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversationId: sessionId,
          query: input
        })
      });

      const data = await res.json();

      setLoading(false);

      if (data.error) {
        setMessages(prev => [...prev, { type: "bot", text: `Error: ${data.error}` }]);
        return;
      }

      if (data.completed) {
        // setMessages(prev => [...prev, { type: "bot", text: data.message }]);
        // setCompleted(true);
        setCompletedData(true);
        setFinaldata(data.data);
        setFields([]);
      } else {
        setSessionId(data.conversationId);
        setFields(data.fields);
        setType(data.type || "text");
        setStep(data.step);
        setOptions(data.options || []);
        setTitle(data.title);
        // After getting API response
        // Add bot message after removing loading
        const timeValue = (data.dbSearchTime && data.dbSearchTime !== "0.00 seconds")
          ? data.dbSearchTime
          : (data.webSearchTime && data.webSearchTime !== "0.00 seconds")
            ? data.webSearchTime
            : null;

        setMessages(prev => [
          ...prev.filter(msg => !msg.loading), // remove loading
          {
            type: "bot",
            text: data.reply,
            ...(timeValue ? { time: timeValue } : {}) // only add if timeValue exists
          }
        ]);
        // setMessages(prev => [...prev, { type: "bot", text: data.reply }]);
      }



      inputRef.current?.focus();
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };



  return (
    <>
      <WorkerHeader />
      <div className="container-fluid mb-4">
        <div className="container-sm">

          <div className="card shadow mt-4 mx-auto chat-wrapper">

            {/* HEADER */}
            <div
              className="header d-flex align-items-center px-3"
              style={{ backgroundColor: "rgb(0, 43, 69)", minHeight: "70px" }}
            >
              <div className="profile me-3">
                <img
                  src="/assets/images/provider_bot.png"
                  alt="profile-avatar"
                  className="rounded-circle"
                  style={{ height: "55px", width: "55px", objectFit: "cover" }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
                <div style={{ color: "#fff" }}>
                  <h4 className="mb-0">Jenny</h4>
                  <small>NACF State Resource Guide</small>
                </div>
              </div>
            </div>

            <div className="p-2">

              {/* CHAT BODY */}
              <div
                ref={chatContainerRef}
                className="chat-body"
              >
                {messages.map((msg, idx) => (
                  <div key={idx}>

                    {/* USER MESSAGE */}
                    {msg.type === "user" && (
                      <div className="user-msg">
                        <p className="mb-0">{msg.text}</p>
                      </div>
                    )}

                    {/* BOT MESSAGE */}
                    {msg.type === "bot" && (
                      <div className="bot-msg">
                        {msg.time && (
                          <div className="mb-2">
                            <span className="text-dark">⏱ {msg.time}</span>
                          </div>
                        )}

                        {msg.loading ? (
                          <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            <span>Thinking...</span>
                          </div>
                        ) : (
                          <p className="mb-0" style={{ whiteSpace: "pre-line" }}>
                            {msg.text}
                          </p>
                        )}
                      </div>
                    )}

                  </div>
                ))}
              </div>

            </div>

            {!completedData && (
              <>
                {type === "text" && (
                  <div className="chat-input d-flex align-items-center gap-2 p-3">
                    <input
                      type="text"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      placeholder="Ask Me Query ..."
                      className="form-control"
                      onKeyDown={handleKeyDown}
                    />

                    <button
                      className="btn bg-white border-0"
                      onClick={() => handleSend()}
                      disabled={!input && type === "text"}
                    >
                      <i className="bi bi-send"></i>
                    </button>
                    <MicButton />
                  </div>
                )}
              </>
            )}

          </div>

          {onboardingStatus && (
            <div className="text-center py-5">

              {onboardingStatus === "success" && (
                <>
                  <div style={{ fontSize: "60px" }}>🎉</div>
                  <h3 className="text-success mt-3">
                    Snapshot submited Successfully!
                  </h3>
                  <p className="text-muted">
                    Thank you for time with us.
                  </p>
                </>
              )}

              {onboardingStatus === "failed" && (
                <>
                  <div style={{ fontSize: "60px" }}>❌</div>
                  <h3 className="text-danger mt-3">
                    Snapshot Failed
                  </h3>
                  <p className="text-muted">
                    Something went wrong. Please try again.
                  </p>

                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => setOnboardingStatus(null)}
                  >
                    Try Again
                  </button>
                </>
              )}

            </div>
          )}


        </div>
      </div>

      {/* Modal */}
      <div
        className="modal fade"
        id="addComplianceModal"
        tabIndex="-1"
        aria-labelledby="addComplianceModalLabel"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="addComplianceModalLabel">
                Sanpshot Overview
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="container-fluid py-3">
                <div className="text-center mb-4">
                  <h3 className="fw-bold text-primary">Snapshot Overview</h3>
                  <p className="text-muted mb-0">
                    Please review your answers before submitting
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WorkerFooter />
    </>
  );
};

export default WorkerBotChat;
